/**
 * useSSE Hook
 * 
 * React hook that manages the SSE connection lifecycle within the authenticated app.
 * 
 * - Connects when the user is authenticated (Auth.accessToken is available)
 * - Disconnects on logout or component unmount
 * - Automatically reconnects with exponential backoff when the connection drops
 * - Passes received events to sseEventHandler
 * - Exposes SSEToastComponent for rendering in Layout.jsx
 * 
 * Integration: Called in Layout.jsx so it is active for all authenticated pages.
 */

import { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { useQueryClient } from '@tanstack/react-query';
import useAuth from '../../hooks/useAuth';
import { pState } from '../../context/profileContext';
import { connect, disconnect } from './sseClient';
import handleSSEEvent from './sseEventHandler';
import { toastState, notificationManager } from './notificationManager';
import Toast from '../../components/toast/Toast';

// Reconnection config
const RECONNECT_BASE_DELAY_MS = 2000;   // 2 seconds initial delay
const RECONNECT_MAX_DELAY_MS  = 30000;  // 30 seconds maximum delay
const RECONNECT_BACKOFF_FACTOR = 2;

const useSSE = () => {
    const { Auth } = useAuth();
    const profileSnap = useSnapshot(pState);
    const toastSnap = useSnapshot(toastState);
    const queryClient = useQueryClient();

    // Refs to manage reconnection state across renders without triggering re-renders
    const reconnectTimerRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;

        // Only connect when we have an access token and an orgId
        if (!Auth?.accessToken || !profileSnap?.currentOrg) {
            return;
        }

        const currentUserId = profileSnap.currentUser;
        const token = Auth.accessToken;

        const clearReconnectTimer = () => {
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        };

        const scheduleReconnect = () => {
            if (!isMountedRef.current) return;

            reconnectAttemptRef.current += 1;
            const delay = Math.min(
                RECONNECT_BASE_DELAY_MS * Math.pow(RECONNECT_BACKOFF_FACTOR, reconnectAttemptRef.current - 1),
                RECONNECT_MAX_DELAY_MS
            );

            console.log(`[useSSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current})`);

            clearReconnectTimer();
            reconnectTimerRef.current = setTimeout(() => {
                if (!isMountedRef.current) return;
                connectSSE();
            }, delay);
        };

        const connectSSE = () => {
            connect(
                token,
                (event) => {
                    // Successful message received — reset reconnect counter
                    reconnectAttemptRef.current = 0;
                    handleSSEEvent(event, currentUserId, queryClient);
                },
                (err) => {
                    // Connection dropped — schedule reconnect with backoff
                    console.warn('[useSSE] SSE connection error, scheduling reconnect:', err?.message || err);
                    scheduleReconnect();
                }
            );
        };

        // Reset reconnect counter on fresh connection (new token or org)
        reconnectAttemptRef.current = 0;
        connectSSE();

        // Cleanup: disconnect and cancel any pending reconnect when component unmounts or auth changes
        return () => {
            isMountedRef.current = false;
            clearReconnectTimer();
            disconnect();
        };
    }, [Auth?.accessToken, profileSnap?.currentOrg]);

    // SSE Toast component — rendered in Layout.jsx
    const SSEToastComponent = toastSnap.current ? (
        <Toast
            type={toastSnap.current.type}
            title={toastSnap.current.title}
            message={toastSnap.current.message}
            values={toastSnap.current.values}
            onClose={() => notificationManager.clear()}
        />
    ) : null;

    return { SSEToastComponent };
};

export default useSSE;
